import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Button, Flex, Separator } from '@radix-ui/themes'
import _ from 'lodash'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { createBudgetGoal } from '~/features/budget/service'
import { CreateBudgetGoalForm } from '~/features/budget/components/BudgetGoalForm'
import { SectionCard } from '~/components/SectionCard'
import { ArrowLeftIcon } from '@radix-ui/react-icons'
import { z } from 'zod'

export const searchParamsSchema = z.object({
  metricID: z.string().optional(),
  stationID: z.string().optional()
})

export type NewBudgetSearchParams = z.infer<typeof searchParamsSchema>

const Component = () => {
  const { queryClient } = Route.useRouteContext()
  const { siteID } = Route.useParams()
  const { metricID, stationID } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { t } = useTranslation(['sites', 'common'])

  const back = () => {
    if (metricID) {
      navigate({
        to: '/sites/$siteID/config/metrics/$metricID',
        params: { siteID, metricID }
      })
    } else if (stationID) {
      navigate({
        to: '/sites/$siteID/config/stations/$stationID',
        params: { siteID, stationID }
      })
    } else {
      navigate({
        to: '/sites/$siteID/config/budget',
        params: { siteID }
      })
    }
  }

  const { mutate: createBudgetGoalMutation, isPending: isBudgetGoalPending } = useMutation({
    mutationFn: createBudgetGoal,
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({
        queryKey: ['budget-goals', siteID]
      })
      navigate({
        to: '/sites/$siteID/config/budget/$goalID',
        params: { siteID, goalID: id }
      })
      toast.success(t('budget.objectives.createSuccess'))
    },
    onError: (error) => {
      console.error('Error creating budget goal', error)
      toast.error(t('budget.objectives.createError'))
    }
  })

  return (
    <Flex gap={'4'} direction={'column'}>
      <SectionCard
        title={t('budget.objectives.createTitle')}
        secondaryActions={
          <Button variant={'ghost'} onClick={back} mr={'2'}>
            <ArrowLeftIcon />
            {t('back', { ns: 'common' })}
          </Button>
        }
      >
        <Separator size={'4'} my={'2'} />
        <CreateBudgetGoalForm
          siteID={siteID}
          onSubmit={createBudgetGoalMutation}
          isPending={isBudgetGoalPending}
          goal={null}
          defaultData={{ metricID, stationID }}
        />
      </SectionCard>
    </Flex>
  )
}

export const Route = createFileRoute('/sites/$siteID/config/budget/new')({
  component: Component,
  validateSearch: (search) => searchParamsSchema.parse(search)
})
